import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { Login } from './pages/auth/Login';
import { Dashboard } from './pages/Dashboard';
import { Users } from './pages/Users';
import InvoiceNew from './pages/InvoiceNew';
import CustomerConfig from './pages/admin/CustomerConfig';
import SewingInstructionUpload from './pages/admin/SewingInstructionUpload';
import EmployeeManagement from './pages/admin/EmployeeManagement';
import ShippingMethods from './pages/admin/ShippingMethods';
import SewingReport from './pages/admin/SewingReport';
import SewerInstructions from './pages/production/SewerInstructions';
import SewerPickup from './pages/production/SewerPickUp';
import CuttingStation from './pages/production/CuttingStation';
import Packaging from './pages/production/Packaging';
import PrintLabel from './pages/production/PrintLabel';
import Stock from './pages/production/Stock';
import Shipping from './pages/production/Shipping';
import OrdersDashboard from './pages/production/OrdersDashboard';
import OrderDetail from './pages/production/OrderDetail';
import CreateReturn from './pages/production/CreateReturn';
import ReturnDetail from './pages/production/ReturnDetail';
import OpenRepairs from './pages/production/OpenRepairs';
import Messages from './pages/Messages';
import OrderChecking from './pages/orders/OrderChecking';
import OrderCheckingDetail from './pages/orders/OrderCheckingDetail';
import NetSuiteInvoicing from './pages/orders/NetSuiteInvoicing';
import PendingOrders from './pages/orders/PendingOrders';
import PendingOrderDetail from './pages/orders/PendingOrderDetail';
import OrderImport from './pages/orders/OrderImport';
import Vehicles from './pages/vehicleApplications/Vehicles';
import PrintPartsList from './pages/vehicleApplications/PrintPartsList';
import AppFinder from './pages/vehicleApplications/AppFinder';
import PublicAppFinder from './pages/vehicleApplications/PublicAppFinder';
import NewPatternForm from './pages/admin/NewPatternForm';
import PriceSheetManagement from './pages/admin/PriceSheetManagement';
import DashCoverPriceSheetManagement from './pages/admin/DashCoverPriceSheetManagement';
import BusinessDashboard from './pages/admin/BusinessDashboard';
import DashcoversSales from './pages/admin/DashcoversSales';
import CustomerReport from './pages/admin/CustomerReport';
import ReturnsRepairsReports from './pages/admin/ReturnsRepairsReports';
import SalesByVehicle from './pages/admin/SalesByVehicle';
import NewCustomer from './pages/admin/NewCustomer';
import ConfiguratorUploads from './pages/admin/ConfiguratorUploads';
import ManufacturingForecast from './pages/admin/ManufacturingForecast';
import InstallationInstructions from './pages/admin/InstallationInstructions';
import InstallationInstructionDetail from './pages/admin/InstallationInstructionDetail';
import WebsiteSalesReports from './pages/automations/WebsiteSalesReports';
import CustomerDatafeeds from './pages/automations/CustomerDatafeeds';
import MarketplaceMessages from './pages/marketplace/MarketplaceMessages';
import { ProtectedRoute } from './components/common/ProtectedRoute';
import { useAuthStore } from './store/authStore';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
    },
  },
});

// Role sets used for requiredRoles (Administrator is always granted by ProtectedRoute automatically)
const ALL_EXCEPT_SEWER = ['Orders', 'Returns', 'CustomerService'];
const ORDERS_CS = ['Orders', 'CustomerService'];
const ORDERS_CS_SEWER = ['Orders', 'CustomerService', 'Sewer'];
const ORDERS_CS_RETURNS = ['Orders', 'CustomerService', 'Returns'];
const ORDERS_ONLY = ['Orders'];
const REPORTING = ['Reporting'];
const REPORTING_MARKETING = ['Reporting', 'Marketing'];

function AppRoutes() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isSewer = useAuthStore((state) => state.user?.roles?.includes('Sewer') ?? false);
  const isReporting = useAuthStore((state) => state.user?.roles?.includes('Reporting') ?? false);
  const isMarketingOnly = useAuthStore((state) => {
    const roles = state.user?.roles ?? [];
    return roles.includes('Marketing') && !roles.includes('Reporting') && !roles.includes('Administrator');
  });
  const defaultRedirect = isSewer
    ? '/production/sewer-instructions'
    : isReporting
    ? '/admin/business-dashboard'
    : isMarketingOnly
    ? '/admin/sales-by-vehicle'
    : '/production/orders-dashboard';

  return (
    <Routes>
      <Route path="/app-finder" element={<PublicAppFinder />} />
      <Route path="/vehicle-applications/vehicles/print" element={<PrintPartsList />} />

      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to={defaultRedirect} replace /> : <Login />}
      />

      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Navigate to={defaultRedirect} replace />
          </ProtectedRoute>
        }
      />

          <Route
            path="/users"
            element={
              <ProtectedRoute requiredRoles={['Administrator']}>
                <Users />
              </ProtectedRoute>
            }
          />

          <Route
            path="/invoice/new"
            element={
              <ProtectedRoute requiredRoles={ALL_EXCEPT_SEWER}>
                <InvoiceNew />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/customer-config"
            element={
              <ProtectedRoute requiredRoles={['Administrator']}>
                <CustomerConfig />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/sewing-instruction-upload"
            element={
              <ProtectedRoute requiredRoles={['Administrator']}>
                <SewingInstructionUpload />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/employee-management"
            element={
              <ProtectedRoute requiredRoles={['Administrator']}>
                <EmployeeManagement />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/shipping-methods"
            element={
              <ProtectedRoute requiredRoles={['Administrator']}>
                <ShippingMethods />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/sewing-report"
            element={
              <ProtectedRoute requiredRoles={['Administrator']}>
                <SewingReport />
              </ProtectedRoute>
            }
          />

          <Route
            path="/production/sewer-instructions"
            element={
              <ProtectedRoute requiredRoles={ORDERS_CS_SEWER}>
                <SewerInstructions />
              </ProtectedRoute>
            }
          />

          <Route
            path="/production/sewer-pickup"
            element={
              <ProtectedRoute requiredRoles={ORDERS_CS_SEWER}>
                <SewerPickup />
              </ProtectedRoute>
            }
          />

          <Route
            path="/production/cutting-orders"
            element={
              <ProtectedRoute requiredRoles={ORDERS_CS}>
                <CuttingStation />
              </ProtectedRoute>
            }
          />

          <Route
            path="/production/packaging"
            element={
              <ProtectedRoute requiredRoles={ORDERS_CS}>
                <Packaging />
              </ProtectedRoute>
            }
          />

          <Route
            path="/production/print-label"
            element={
              <ProtectedRoute requiredRoles={ORDERS_CS}>
                <PrintLabel />
              </ProtectedRoute>
            }
          />

          <Route
            path="/stock"
            element={
              <ProtectedRoute requiredRoles={ORDERS_CS}>
                <Stock />
              </ProtectedRoute>
            }
          />

          <Route
            path="/shipping"
            element={
              <ProtectedRoute requiredRoles={ORDERS_CS}>
                <Shipping />
              </ProtectedRoute>
            }
          />

          <Route
            path="/production/orders-dashboard"
            element={
              <ProtectedRoute requiredRoles={ALL_EXCEPT_SEWER}>
                <OrdersDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/production/orders/:orderId"
            element={
              <ProtectedRoute requiredRoles={ALL_EXCEPT_SEWER}>
                <OrderDetail />
              </ProtectedRoute>
            }
          />

          <Route
            path="/production/orders/:orderId/return"
            element={
              <ProtectedRoute requiredRoles={ALL_EXCEPT_SEWER}>
                <CreateReturn />
              </ProtectedRoute>
            }
          />

          <Route
            path="/production/returns/:returnId"
            element={
              <ProtectedRoute requiredRoles={ALL_EXCEPT_SEWER}>
                <ReturnDetail />
              </ProtectedRoute>
            }
          />

          <Route
            path="/production/open-repairs"
            element={
              <ProtectedRoute requiredRoles={ORDERS_CS_RETURNS}>
                <OpenRepairs />
              </ProtectedRoute>
            }
          />

          <Route
            path="/messages"
            element={
              <ProtectedRoute requiredRoles={ALL_EXCEPT_SEWER}>
                <Messages />
              </ProtectedRoute>
            }
          />

          <Route
            path="/orders/pending"
            element={
              <ProtectedRoute requiredRoles={ALL_EXCEPT_SEWER}>
                <PendingOrders />
              </ProtectedRoute>
            }
          />

          <Route
            path="/orders/pending/:id"
            element={
              <ProtectedRoute requiredRoles={ALL_EXCEPT_SEWER}>
                <PendingOrderDetail />
              </ProtectedRoute>
            }
          />

          <Route
            path="/orders/import"
            element={
              <ProtectedRoute requiredRoles={ALL_EXCEPT_SEWER}>
                <OrderImport />
              </ProtectedRoute>
            }
          />

          <Route
            path="/orders/check"
            element={
              <ProtectedRoute requiredRoles={ALL_EXCEPT_SEWER}>
                <OrderChecking />
              </ProtectedRoute>
            }
          />

          <Route
            path="/orders/check/:invoiceNumber"
            element={
              <ProtectedRoute requiredRoles={ALL_EXCEPT_SEWER}>
                <OrderCheckingDetail />
              </ProtectedRoute>
            }
          />

          <Route
            path="/orders/netsuite-invoicing"
            element={
              <ProtectedRoute requiredRoles={ALL_EXCEPT_SEWER}>
                <NetSuiteInvoicing />
              </ProtectedRoute>
            }
          />

          <Route
            path="/vehicle-applications/vehicles"
            element={
              <ProtectedRoute>
                <Vehicles />
              </ProtectedRoute>
            }
          />

          <Route
            path="/vehicle-applications/app-finder"
            element={
              <ProtectedRoute requiredRoles={ORDERS_ONLY}>
                <AppFinder />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/new-pattern-form"
            element={
              <ProtectedRoute requiredRoles={['Administrator']}>
                <NewPatternForm />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/price-sheet-management"
            element={
              <ProtectedRoute requiredRoles={['Administrator']}>
                <PriceSheetManagement />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/dash-cover-price-sheet-management"
            element={
              <ProtectedRoute requiredRoles={['Administrator']}>
                <DashCoverPriceSheetManagement />
              </ProtectedRoute>
            }
          />

          <Route
            path="/automations/website-sales-reports"
            element={
              <ProtectedRoute requiredRoles={[]}>
                <WebsiteSalesReports />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/business-dashboard"
            element={
              <ProtectedRoute requiredRoles={REPORTING}>
                <BusinessDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/dashcovers-sales"
            element={
              <ProtectedRoute requiredRoles={REPORTING}>
                <DashcoversSales />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/customer-report/:customerId"
            element={
              <ProtectedRoute requiredRoles={REPORTING}>
                <CustomerReport />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/returns-repairs-reports"
            element={
              <ProtectedRoute requiredRoles={REPORTING}>
                <ReturnsRepairsReports />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/sales-by-vehicle"
            element={
              <ProtectedRoute requiredRoles={REPORTING_MARKETING}>
                <SalesByVehicle />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/manufacturing-forecast"
            element={
              <ProtectedRoute requiredRoles={REPORTING}>
                <ManufacturingForecast />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/new-customer"
            element={
              <ProtectedRoute requiredRoles={['Administrator']}>
                <NewCustomer />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/configurator-uploads"
            element={
              <ProtectedRoute requiredRoles={['Administrator']}>
                <ConfiguratorUploads />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/installation-instructions"
            element={
              <ProtectedRoute requiredRoles={['Administrator']}>
                <InstallationInstructions />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/installation-instructions/:id"
            element={
              <ProtectedRoute requiredRoles={['Administrator']}>
                <InstallationInstructionDetail />
              </ProtectedRoute>
            }
          />

          <Route
            path="/automations/customer-datafeeds"
            element={
              <ProtectedRoute requiredRoles={[]}>
                <CustomerDatafeeds />
              </ProtectedRoute>
            }
          />

          <Route
            path="/marketplace/messages"
            element={
              <ProtectedRoute requiredRoles={ALL_EXCEPT_SEWER}>
                <MarketplaceMessages />
              </ProtectedRoute>
            }
          />

          <Route
            path="/unauthorized"
            element={
              <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <div className="text-center">
                  <h1 className="text-4xl font-bold text-gray-900 dark:text-white">403</h1>
                  <p className="text-xl text-gray-600 dark:text-gray-400 mt-2">Unauthorized Access</p>
                </div>
              </div>
            }
          />

          <Route
            path="*"
            element={
              <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <div className="text-center">
                  <h1 className="text-4xl font-bold text-gray-900 dark:text-white">404</h1>
                  <p className="text-xl text-gray-600 dark:text-gray-400 mt-2">Page Not Found</p>
                </div>
              </div>
            }
          />
    </Routes>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AppRoutes />
      </Router>
      <Toaster position="top-right" />
    </QueryClientProvider>
  );
}

export default App;
